// The template composer canvas and its editor pieces (box editor, populate dialog).
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { dsComposerApi, projectSchemaApi, adminDynamicApi } from '../../services/api';
import { X, GripVertical, Settings2 } from 'lucide-react';
import { revRows, sectionRows } from '../development/dsTemplateLayout';
import { CALC_FUNCTIONS, CALC_LISTS, isDsValues, isCalc, listNameOf, listBinding, parseCalc, calcBinding } from '../development/dsCalc';
import bodyRowPairs from '../development/dsBodyRows';
import { FONT, THIN, THICK, ROW_H, base, SECTION_TYPES, SECTION_HEADS, CAL_COLS, CAL_HEADS, COMP_COLS, COMP_HEADS, STATIC_SECTIONS, isUnsaved, LIGHT_FIELD, hasStaticContent, splitFieldSource, contentFor, contentLinked, zoneLabelFor, rowOrder, rebalanceRow, redistributeAround, placeBox, getHeaderRow, getCenterBoxes, getBodyLine, getSectionCells, placeZones, newBoxId, freeLines, usedLines, appendRows, stampRows, rowCells, suffixCells, parseGrid, TABLE_W, REV_COLS, REV_LAST } from './dsBoxes';
import StuiSelect from '../../components/common/StuiSelect';

const PALETTE_BOXES = [
  { type: 'value',   label: 'Value'          },
  { type: 'label',   label: 'Label'          },
  { type: 'section', label: 'Section Header' },
];

// Standard text for a deliberately empty row, so it reads the same on every datasheet.
const BLANK_LABEL_TEXT = 'Left Blank Intentionally';

// The draggable chips. Blank is a palette SHORTCUT, not a fourth box type - it drops an
// ordinary label box, so DS_BoxType, every renderer and the PDF stay as they are.
//
// It sets the TAG side to the standard text and the INDEX side to an empty string. A box
// already carries both, and the index side falls back to the tag side only when it is null -
// an empty string stops that fallback. So the template composer shows "Left Blank
// Intentionally" while the index view and the printed datasheet show nothing, which is the
// whole point of the row. Both sides stay editable like any other label afterwards.
const PALETTE_CHIPS = [
  ...PALETTE_BOXES,
  { type: 'label', label: BLANK_LABEL_TEXT, labelIdx: '', chip: 'Blank' },
];

// Admin tables a box may bind to. Entered values live in each project's DSValues, not admin.
const ADMIN_DS_TABLES = ['UtlCfg_DSConfig', 'UtlCfg_DSIndex'];

// One editable content side (tag-based or index-based). Owns its own label,
// value-source, db-source and table/field state plus the column fetch, so the
// tag and index editors are the same code rather than two copies of it.
function useContentSide(initSource, initLabel) {
  const initUser    = isDsValues(initSource);
  const initCalc    = parseCalc(initSource);
  const parsed      = splitFieldSource(initSource || '');
  const initIsAdmin = (initSource || '').startsWith('admin:');

  const [label,       setLabel]       = useState(initLabel || '');
  const [valueSource, setValueSource] = useState(initUser ? 'dsvalues' : isCalc(initSource) ? 'calc' : 'field');
  const [dbSource,    setDbSource]    = useState(initIsAdmin ? 'admin' : 'project');
  const [fieldTable,  setFieldTable]  = useState(parsed.table);
  const [fieldName,   setFieldName]   = useState(parsed.name);
  // A dsvalues box may offer a fixed dropdown list instead of free text.
  const [listName,    setListName]    = useState(listNameOf(initSource) || '');
  // A calculated box: a registry function and one line reference ('506' / '506.2') per argument.
  const [calcFn,      setCalcFn]      = useState(initCalc?.fn || '');
  const [calcArgs,    setCalcArgs]    = useState(
    () => (initCalc?.refs || []).map(r => (r.nth > 1 ? `${r.line}.${r.nth}` : String(r.line))));
  const [columns,     setColumns]     = useState([]);
  const [loadingCols, setLoadingCols] = useState(false);

  useEffect(() => {
    if (!fieldTable) { setColumns([]); return; }
    setLoadingCols(true);
    const schemaApi = dbSource === 'admin' ? adminDynamicApi : projectSchemaApi;
    schemaApi.getColumns(fieldTable)
      .then(r => setColumns(r.data ?? []))
      .catch(() => setColumns([]))
      .finally(() => setLoadingCols(false));
  }, [fieldTable, dbSource]);

  const changeDbSource = (src) => {
    setDbSource(src);
    setFieldTable('');
    setFieldName('');
    setColumns([]);
  };

  const changeTable = (t) => {
    setFieldTable(t);
    setFieldName('');
  };

  const changeCalcFn = (fn) => {
    setCalcFn(fn);
    const arity = CALC_FUNCTIONS[fn]?.args.length ?? 0;
    setCalcArgs(prev => Array.from({ length: arity }, (_, i) => prev[i] ?? ''));
  };

  const setCalcArg = (i, ref) => setCalcArgs(prev => prev.map((r, j) => (j === i ? ref : r)));

  // Collapses this side's inputs back into the box fields, exactly as the
  // single-sided editor used to.
  const resolve = (type) => {
    if (type === 'value' && valueSource === 'dsvalues') {
      return { label, fieldSource: listBinding(listName), fieldTable: '', fieldName: '' };
    }
    if (type === 'value' && valueSource === 'calc') {
      // An argument left unpicked is written as @0, a line no template has, so the box reads
      // as unresolved (blank) instead of silently binding the wrong line.
      const refs = calcArgs.map(a => {
        const [line, nth] = String(a || '').split('.');
        return { line: Number(line) || 0, nth: Number(nth) || 1 };
      });
      return { label, fieldSource: calcFn ? calcBinding(calcFn, refs) : '', fieldTable: '', fieldName: '' };
    }
    const fieldSource = fieldTable && fieldName
      ? (dbSource === 'admin' ? `admin:${fieldTable}.${fieldName}` : `${fieldTable}.${fieldName}`)
      : '';
    return { label, fieldSource, fieldTable, fieldName };
  };

  return { label, setLabel, valueSource, setValueSource, dbSource, changeDbSource,
           fieldTable, changeTable, fieldName, setFieldName, columns, loadingCols,
           listName, setListName, calcFn, changeCalcFn, calcArgs, setCalcArg, resolve };
}

const VALUE_SOURCES = [
  { v: 'field',    label: 'Data Defined' },
  { v: 'dsvalues', label: 'User Defined Per Index Sheet' },
  { v: 'calc',     label: 'Calculated' },
];

// Every value box of the template (any sheet) as a calc argument choice: "506 – Height".
// The reference is the line plus, when the line holds more than one value box, its position -
// the same '@506.2' the evaluator reads, counted within the box's own sheet.
function calcArgOptions(templateBoxes, selfId) {
  const byOrder = (a, b) => (a.order ?? 1) - (b.order ?? 1);
  const sameLine = (a, b) => a.lineNo === b.lineNo && (a.sheet ?? 1) === (b.sheet ?? 1);
  const values = templateBoxes.filter(b => b.type === 'value');
  const labelOf = b => templateBoxes
    .filter(o => o.type !== 'value' && sameLine(o, b))
    .sort(byOrder)
    .map(o => o.label)
    .filter(Boolean)
    .join(' ');
  return values
    .filter(b => b.id !== selfId)
    .map(b => {
      const nth = values.filter(o => sameLine(o, b)).sort(byOrder).indexOf(b) + 1;
      const ref = nth > 1 ? `${b.lineNo}.${nth}` : String(b.lineNo);
      return { ref, sheet: b.sheet ?? 1, text: `${ref} – ${labelOf(b) || '(no label)'}` };
    })
    .sort((a, b) => a.sheet - b.sheet || parseFloat(a.ref) - parseFloat(b.ref))
    // Two sheets can carry the same line number; one reference covers both.
    .filter((o, i, all) => all.findIndex(x => x.ref === o.ref) === i);
}

function ContentSide({ side, type, tables, readOnly, templateBoxes = [], selfId = null }) {
  const showFieldPicker = type === 'value' && side.valueSource === 'field';
  const showListPicker  = type === 'value' && side.valueSource === 'dsvalues';
  const showCalcPicker  = type === 'value' && side.valueSource === 'calc';
  const argOptions = useMemo(
    () => (showCalcPicker ? calcArgOptions(templateBoxes, selfId) : []),
    [showCalcPicker, templateBoxes, selfId]
  );
  const calcDef = CALC_FUNCTIONS[side.calcFn];
  const rid = React.useId();
  // The overlay deliberately has no onClick dismiss. A stray click - or merely releasing
  // the mouse off a select or datalist after using it - delivered a click to the overlay
  // and threw away everything typed. Closing is explicit: the header X or Cancel.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 0 }}>
      {type !== 'value' && (
        <div className="stui-field-group">
          <label className="stui-field-label">Label</label>
          <input className="stui-field-input" style={LIGHT_FIELD} value={side.label} disabled={readOnly}
            onChange={e => side.setLabel(e.target.value)} placeholder="Box label" />
        </div>
      )}
      {type === 'value' && (
        <div className="stui-field-group">
          <label className="stui-field-label">Value Source</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {VALUE_SOURCES.map(opt => (
              <label key={opt.v} className="stui-checkbox">
                <input type="radio" className="stui-radio-input" name={`vs-${rid}`} value={opt.v} disabled={readOnly}
                  checked={side.valueSource === opt.v} onChange={() => side.setValueSource(opt.v)} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
      {showListPicker && (
        <div className="stui-field-group">
          <label className="stui-field-label">Dropdown List</label>
          <StuiSelect className="stui-select" value={side.listName} disabled={readOnly}
            onChange={e => side.setListName(e.target.value)}>
            <option value="">None (free text)</option>
            {Object.keys(CALC_LISTS).map(n => <option key={n} value={n}>{n}</option>)}
          </StuiSelect>
        </div>
      )}
      {showCalcPicker && (
        <>
          <div className="stui-field-group">
            <label className="stui-field-label">Function</label>
            <StuiSelect className="stui-select" value={side.calcFn} disabled={readOnly}
              onChange={e => side.changeCalcFn(e.target.value)}>
              <option value="">— select function —</option>
              {Object.entries(CALC_FUNCTIONS).map(([n, def]) => <option key={n} value={n}>{def.label} ({n})</option>)}
            </StuiSelect>
          </div>
          {calcDef?.args.map((argLabel, i) => (
            <div key={argLabel} className="stui-field-group">
              <label className="stui-field-label">{argLabel}</label>
              <StuiSelect className="stui-select" value={side.calcArgs[i] ?? ''} disabled={readOnly}
                onChange={e => side.setCalcArg(i, e.target.value)}>
                <option value="">— select line —</option>
                {argOptions.map(o => <option key={o.ref} value={o.ref}>{o.text}</option>)}
                {side.calcArgs[i] && !argOptions.some(o => o.ref === side.calcArgs[i]) && (
                  <option value={side.calcArgs[i]}>{side.calcArgs[i]} – not on this template</option>
                )}
              </StuiSelect>
            </div>
          ))}
          {side.calcFn && (
            <div className="stui-label-xs-muted">Source: <code>{side.resolve('value').fieldSource}</code></div>
          )}
        </>
      )}
      {showFieldPicker && (
        <>
          <div className="stui-field-group">
            <label className="stui-field-label">Source Database</label>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              {[{ v: 'project', label: 'Project DB' }, { v: 'admin', label: 'Admin DB' }].map(opt => (
                <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: readOnly ? 'default' : 'pointer' }}>
                  <input type="radio" name={`db-${rid}`} value={opt.v} disabled={readOnly}
                    checked={side.dbSource === opt.v} onChange={() => side.changeDbSource(opt.v)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">Table</label>
            <StuiSelect className="stui-select" value={side.fieldTable} disabled={readOnly}
              onChange={e => side.changeTable(e.target.value)}>
              <option value="">— select table —</option>
              {side.dbSource === 'admin'
                ? ADMIN_DS_TABLES.map(t => <option key={t} value={t}>{t}</option>)
                : tables.map(t => <option key={t} value={t}>{t}</option>)
              }
            </StuiSelect>
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">Field</label>
            <StuiSelect className="stui-select" value={side.fieldName} disabled={readOnly || !side.fieldTable || side.loadingCols}
              onChange={e => side.setFieldName(e.target.value)}>
              <option value="">{side.loadingCols ? 'Loading…' : '— select field —'}</option>
              {side.columns.map(c => <option key={c} value={c}>{c}</option>)}
            </StuiSelect>
          </div>
          {side.fieldTable && side.fieldName && (
            <div className="stui-label-xs-muted">Source: <code>{side.dbSource === 'admin' ? 'admin:' : ''}{side.fieldTable}.{side.fieldName}</code></div>
          )}
        </>
      )}
    </div>
  );
}

function BoxEditModal({ box, onSave, onClose, templateBoxes = [] }) {
  const [type,    setType]    = useState(box.type    || 'label');
  const [colSpan, setColSpan] = useState(box.colSpan || 12);
  const [tables,  setTables]  = useState([]);

  // "Same" is inferred from the stored values rather than read from a flag.
  const [linked, setLinked] = useState(contentLinked(box));

  const tagSide = useContentSide(contentFor(box, 'tag'), box.type === 'value' ? '' : contentFor(box, 'tag'));
  const idxSide = useContentSide(contentFor(box, 'idx'), box.type === 'value' ? '' : contentFor(box, 'idx'));

  useEffect(() => {
    projectSchemaApi.getTables()
      .then(r => setTables(r.data ?? []))
      .catch(() => {});
  }, []);

  const handleSave = () => {
    const tag = tagSide.resolve(type);
    // While linked, the index side is not edited - it is written as a copy of the
    // tag side, which is what makes the two compare equal on the next open.
    const idx = linked ? tag : idxSide.resolve(type);
    onSave({
      ...box, type, colSpan,
      label:       tag.label,
      fieldSource: tag.fieldSource,
      fieldTable:  tag.fieldTable,
      fieldName:   tag.fieldName,
      labelIdx:       idx.label,
      fieldSourceIdx: idx.fieldSource,
      fieldTableIdx:  idx.fieldTable,
      fieldNameIdx:   idx.fieldName,
    });
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="stui-modal-overlay">
      <div className="stui-modal" style={{ maxWidth: linked ? 380 : 720, width: '90vw' }}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">Edit Box</span>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="stui-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Structure is shared by both sides; only content splits. */}
          <div className="stui-field-group">
            <label className="stui-field-label">Type</label>
            <StuiSelect className="stui-select" value={type} onChange={e => setType(e.target.value)}>
              {PALETTE_BOXES.map(p => <option key={p.type} value={p.type}>{p.label}</option>)}
            </StuiSelect>
          </div>
          <div className="stui-field-group">
            <label className="stui-field-label">Width — {colSpan} / 12</label>
            <input type="range" min={1} max={12} value={colSpan} onChange={e => setColSpan(Number(e.target.value))} style={{ width: '100%', marginTop: 4 }} />
          </div>

          <div className="stui-field-group">
            <label className="stui-field-label">Content</label>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              {[{ v: true, label: 'Same for Tag Based and Index Based' }, { v: false, label: 'Different' }].map(opt => (
                <label key={String(opt.v)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="radio" name="content-linked" checked={linked === opt.v} onChange={() => setLinked(opt.v)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {linked ? (
            <ContentSide side={tagSide} type={type} tables={tables} templateBoxes={templateBoxes} selfId={box.id} />
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="stui-help-section-heading" style={{ marginBottom: 6 }}>Tag Based</div>
                <ContentSide side={tagSide} type={type} tables={tables} templateBoxes={templateBoxes} selfId={box.id} />
              </div>
              <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-gray-200)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="stui-help-section-heading" style={{ marginBottom: 6 }}>Index Based</div>
                <ContentSide side={idxSide} type={type} tables={tables} templateBoxes={templateBoxes} selfId={box.id} />
              </div>
            </div>
          )}
          {linked && (
            <div className="stui-label-xs-muted">
              Index Based will be saved as a copy of Tag Based. Choose Different to set it independently.
            </div>
          )}
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="stui-btn stui-btn-primary" onClick={handleSave}>Apply</button>
        </div>
      </div>
    </div>,
    document.body
  );
}


// ── Populate Modal ────────────────────────────────────────────────────────────
//
// Four ways to fill rows without dragging one box at a time. The first three all
// end in appendRows(); the fourth edits existing boxes in place. Every one of
// them mutates the same in-memory boxes state, so the save diff picks the result
// up with no new endpoints involved.

const POPULATE_MODES = [
  { key: 'repeat',  label: 'Repeat Row' },
  { key: 'library', label: 'Pick From Library' },
  { key: 'paste',   label: 'Paste From Excel' },
  { key: 'grid',    label: 'Edit As Table' },
];

const COL_TYPES = [
  { v: 'label', label: 'Label' },
  { v: 'value', label: 'Value' },
  { v: 'skip',  label: 'Skip' },
];

export function PopulateModal({ boxes, setBoxes, onClose, notify, layout = null }) {
  const zones = useMemo(() => placeZones(layout), [layout]);
  const [mode,    setMode]    = useState('repeat');
  const [zoneKey, setZoneKey] = useState('bodyL');

  const free = freeLines(boxes, zoneKey, zones);
  const used = usedLines(boxes, zoneKey, zones);

  // ── repeat ──
  const [srcLine, setSrcLine] = useState('');
  // Which lines receive the pattern. Chosen explicitly, so a stamp is no longer limited to the
  // next free rows and can overwrite rows that already have content.
  const [targetLines, setTargetLines] = useState([]);
  // Off by default: stamping the same row verbatim is the common case, and renumbering
  // labels is the exception you opt into.
  const [suffix,  setSuffix]  = useState(false);

  // A zone change invalidates any line numbers picked in the previous zone.
  useEffect(() => { setTargetLines([]); }, [zoneKey]);

  // Every line in the zone with a short preview of what is on it, so the picker shows which
  // rows would be overwritten. The pattern row is left out: stamping it onto itself is a no-op
  // at best and destructive at worst.
  const zoneLineChoices = (zones.find(z => z.key === zoneKey)?.lines ?? [])
    .filter(n => String(n) !== String(srcLine))
    .map(n => {
      const cells = rowCells(boxes, zoneKey, n, zones);
      const preview = cells
        .map(c => c.type === 'value' ? (c.fieldSource || '[value]') : (c.label || '[blank]'))
        .join(' | ');
      return { n, preview: preview.slice(0, 48) };
    });

  // ── library ──
  const [library,    setLibrary]    = useState(null);
  const [libLoading, setLibLoading] = useState(false);
  const [libFilter,  setLibFilter]  = useState('');
  const [libPicked,  setLibPicked]  = useState([]);
  const [libWithValue, setLibWithValue] = useState(true);

  useEffect(() => {
    if (mode !== 'library' || library !== null) return;
    setLibLoading(true);
    dsComposerApi.getContentLibrary()
      .then(r => setLibrary(r.data ?? { labels: [], values: [], sections: [] }))
      .catch(() => setLibrary({ labels: [], values: [], sections: [] }))
      .finally(() => setLibLoading(false));
  }, [mode, library]);

  const libLabels = (library?.labels ?? [])
    .filter(l => l.toLowerCase().includes(libFilter.toLowerCase()));

  // ── paste ──
  const [pasteText, setPasteText] = useState('');
  const parsed = parseGrid(pasteText);
  const colCount = parsed.reduce((m, r) => Math.max(m, r.length), 0);
  const [colTypes, setColTypes] = useState([]);
  const effColTypes = Array.from({ length: colCount },
    (_, i) => colTypes[i] ?? (i === 0 ? 'label' : i === 1 ? 'value' : 'label'));

  // ── shared apply ──
  const apply = (rows, what) => {
    if (rows.length === 0) { notify.error('Nothing to add.'); return; }
    const res = appendRows(boxes, zoneKey, rows, zones);
    if (res.placed === 0) {
      notify.error(`No free lines left in ${zones.find(z => z.key === zoneKey)?.label ?? 'this zone'}.`);
      return;
    }
    setBoxes(() => res.boxes);
    if (res.skipped > 0) {
      notify.warning(`Added ${res.placed} ${what}; ${res.skipped} did not fit in the zone and were skipped.`);
    } else {
      notify.success(`Added ${res.placed} ${what}.`);
    }
    onClose();
  };

  const doRepeat = () => {
    const cells = rowCells(boxes, zoneKey, Number(srcLine), zones);
    if (cells.length === 0) { notify.error('Pick a row that has boxes on it.'); return; }
    const targets = [...targetLines].sort((a, b) => a - b);
    if (targets.length === 0) { notify.error('Pick at least one row to modify.'); return; }

    // Numbering follows the target order, so the suffixes read 2, 3, 4 down the sheet whichever
    // rows were picked.
    const rows = targets.map((_, i) => suffix ? suffixCells(cells, i + 2) : cells);
    const res = stampRows(boxes, zoneKey, targets, rows, zones);
    setBoxes(() => res.boxes);
    if (res.replaced > 0) {
      notify.warning(`Stamped ${res.placed} row${res.placed !== 1 ? 's' : ''}, replacing content on ${res.replaced}.`);
    } else {
      notify.success(`Stamped ${res.placed} row${res.placed !== 1 ? 's' : ''}.`);
    }
    onClose();
  };

  const doLibrary = () => {
    const rows = libPicked.map(l => libWithValue
      ? [{ type: 'label', label: l }, { type: 'value', fieldSource: '' }]
      : [{ type: 'label', label: l }]);
    apply(rows, `row${rows.length !== 1 ? 's' : ''}`);
  };

  const doPaste = () => {
    const rows = parsed
      .map(cells => cells
        .map((text, ci) => ({ text, t: effColTypes[ci] }))
        .filter(c => c.t !== 'skip')
        .map(c => c.t === 'value'
          ? { type: 'value', fieldSource: '' }
          : { type: 'label', label: c.text }))
      .filter(r => r.length > 0);
    apply(rows, `row${rows.length !== 1 ? 's' : ''}`);
  };

  // ── grid: edits existing boxes in place ──
  const flat = [...boxes]
    .filter(b => b.zone === 'header' || b.zone === 'body')
    .sort((x, y) => x.lineNo - y.lineNo || (x.order ?? 0) - (y.order ?? 0));

  const editBox = (id, patch) => setBoxes(prev =>
    prev.map(b => b.id === id ? { ...b, ...patch, _dirty: true } : b));

  const zoneLabel = zones.find(z => z.key === zoneKey)?.label ?? '';

  return ReactDOM.createPortal(
    <div className="stui-modal-overlay">
      <div className="stui-modal" onClick={e => e.stopPropagation()}
        style={{ maxWidth: mode === 'grid' ? 860 : 620, width: '94vw' }}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">Populate Rows</span>
          <button className="stui-modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 2, borderBottom: 'var(--border-width) solid var(--color-border-subtle)', padding: '0 12px' }}>
          {POPULATE_MODES.map(m => (
            <button key={m.key} className={`stui-dataentry-tab${mode === m.key ? ' active' : ''}`}
              onClick={() => setMode(m.key)}>{m.label}</button>
          ))}
        </div>

        <div className="stui-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode !== 'grid' && (
            <div className="stui-field-group">
              <label className="stui-field-label">Target zone</label>
              <StuiSelect className="stui-select" value={zoneKey} onChange={e => setZoneKey(e.target.value)}>
                {zones.map(z => <option key={z.key} value={z.key}>{z.label}</option>)}
              </StuiSelect>
              <p className="stui-hint">{free.length} free line{free.length !== 1 ? 's' : ''} in {zoneLabel}. New rows fill the lowest free lines in order.</p>
            </div>
          )}

          {mode === 'repeat' && (
            <>
              <div className="stui-field-group">
                <label className="stui-field-label">Pattern row</label>
                <StuiSelect className="stui-select" value={srcLine} onChange={e => setSrcLine(e.target.value)}>
                  <option value="">— select a populated row —</option>
                  {used.map(n => {
                    const cells = rowCells(boxes, zoneKey, n, zones);
                    const preview = cells.map(c => c.type === 'value' ? (c.fieldSource || '[value]') : (c.label || '[blank]')).join(' | ');
                    return <option key={n} value={n}>Line {n} — {preview.slice(0, 70)}</option>;
                  })}
                </StuiSelect>
                {used.length === 0 && <p className="stui-hint">No populated rows in this zone yet.</p>}
              </div>
              <div className="stui-field-group">
                <label className="stui-field-label">Rows to be modified</label>
                {/* Every line in the zone, not just the free ones: picking a populated row
                    replaces what is on it, which is the point of "modified". The pattern row
                    itself is excluded so it cannot overwrite itself. */}
                <div className="stui-checkbox-list" style={{ maxHeight: 220 }}>
                  {zoneLineChoices.length === 0 && (
                    <div className="stui-label-xs-muted" style={{ padding: 8 }}>No lines in this zone.</div>
                  )}
                  {zoneLineChoices.map(({ n, preview }) => (
                    <label key={n} className="stui-checkbox-list-item">
                      <input type="checkbox" checked={targetLines.includes(n)}
                        onChange={() => setTargetLines(prev => prev.includes(n)
                          ? prev.filter(x => x !== n)
                          : [...prev, n])} />
                      <span className="stui-checkbox-list-item-name">
                        Line {n}{preview ? ` — ${preview}` : ' — empty'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="stui-btn-row">
                <button className="stui-btn stui-btn-secondary stui-btn-sm"
                  onClick={() => setTargetLines(zoneLineChoices.filter(c => !c.preview).map(c => c.n))}>
                  Select All Empty
                </button>
                <button className="stui-btn stui-btn-secondary stui-btn-sm"
                  onClick={() => setTargetLines(zoneLineChoices.map(c => c.n))}>
                  Select All
                </button>
                <button className="stui-btn stui-btn-secondary stui-btn-sm"
                  onClick={() => setTargetLines([])}>
                  Clear
                </button>
              </div>
              <p className="stui-hint">
                {targetLines.length} row{targetLines.length !== 1 ? 's' : ''} selected
                {(() => {
                  const overwrite = zoneLineChoices.filter(c => targetLines.includes(c.n) && c.preview).length;
                  return overwrite > 0 ? ` — ${overwrite} already have content and will be replaced` : '';
                })()}
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" className="stui-checkbox-input" checked={suffix} onChange={e => setSuffix(e.target.checked)} />
                Append an incrementing number to label text (Body Type 2, Body Type 3, …)
              </label>
            </>
          )}

          {mode === 'library' && (
            <>
              <div className="stui-field-group">
                <label className="stui-field-label">Search labels already used in other templates</label>
                <input className="stui-field-input" style={LIGHT_FIELD} value={libFilter} onChange={e => setLibFilter(e.target.value)} placeholder="filter…" />
              </div>
              <div className="stui-checkbox-list" style={{ maxHeight: 260 }}>
                {libLoading && <div className="stui-label-xs-muted" style={{ padding: 8 }}>Loading library…</div>}
                {!libLoading && libLabels.length === 0 && <div className="stui-label-xs-muted" style={{ padding: 8 }}>No matching labels.</div>}
                {libLabels.slice(0, 400).map(l => (
                  <label key={l} className="stui-checkbox-list-item">
                    <input type="checkbox" className="stui-checkbox-input" checked={libPicked.includes(l)}
                      onChange={() => setLibPicked(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l])} />
                    <span className="stui-checkbox-list-item-name">{l}</span>
                  </label>
                ))}
              </div>
              <div className="stui-btn-row">
                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setLibPicked(libLabels)}>Select All Shown</button>
                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setLibPicked([])}>Clear</button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" className="stui-checkbox-input" checked={libWithValue} onChange={e => setLibWithValue(e.target.checked)} />
                Add an unbound value box beside each label
              </label>
              <p className="stui-hint">{libPicked.length} label{libPicked.length !== 1 ? 's' : ''} selected</p>
            </>
          )}

          {mode === 'paste' && (
            <>
              <div className="stui-field-group">
                <label className="stui-field-label">Paste a block from Excel</label>
                <textarea className="stui-field-input" rows={7} value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  placeholder={'Body Type\tStyle\nBonnet Style\tBolted'} style={{ ...LIGHT_FIELD, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                <p className="stui-hint">Tab-separated (straight from Excel) or comma-separated. One line becomes one row.</p>
              </div>
              {colCount > 0 && (
                <>
                  <div className="stui-field-group">
                    <label className="stui-field-label">Column types</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      {Array.from({ length: colCount }, (_, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span className="stui-label-xs-muted">Col {i + 1}</span>
                          <StuiSelect className="stui-select" style={{ minWidth: 88 }} value={effColTypes[i]}
                            onChange={e => setColTypes(prev => {
                              const next = [...effColTypes];
                              next[i] = e.target.value;
                              return next;
                            })}>
                            {COL_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                          </StuiSelect>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ maxHeight: 180, overflow: 'auto', border: 'var(--border-width) solid var(--color-border-subtle)' }}>
                    <table className="stui-inner-table" style={{ fontSize: 11, width: '100%' }}>
                      <thead><tr><th>#</th>{Array.from({ length: colCount }, (_, i) => <th key={i}>{effColTypes[i]}</th>)}</tr></thead>
                      <tbody>
                        {parsed.slice(0, 30).map((r, ri) => (
                          <tr key={ri}>
                            <td className="stui-label-xs-muted">{ri + 1}</td>
                            {Array.from({ length: colCount }, (_, ci) => (
                              <td key={ci} style={{ opacity: effColTypes[ci] === 'skip' ? 0.35 : 1 }}>{r[ci] ?? ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="stui-hint">
                    {parsed.length} row{parsed.length !== 1 ? 's' : ''} parsed
                    {parsed.length > free.length && ` — only ${free.length} will fit in ${zoneLabel}`}
                  </p>
                </>
              )}
            </>
          )}

          {mode === 'grid' && (
            <>
              <p className="stui-hint">Every header and body box, top to bottom. Edits here save with the rest of the layout.</p>
              <div style={{ maxHeight: 420, overflow: 'auto', border: 'var(--border-width) solid var(--color-border-subtle)' }}>
                <table className="stui-inner-table" style={{ fontSize: 11, width: '100%' }}>
                  <thead>
                    <tr><th style={{ width: 48 }}>Line</th><th style={{ width: 70 }}>Zone</th>
                      <th style={{ width: 96 }}>Type</th><th>Content</th><th style={{ width: 66 }}>Width</th></tr>
                  </thead>
                  <tbody>
                    {flat.map(b => (
                      <tr key={b.id}>
                        <td>{b.lineNo}</td>
                        <td className="stui-label-xs-muted">{zoneLabelFor(b, layout)}</td>
                        <td>
                          <StuiSelect className="stui-select" style={{ fontSize: 11 }} value={b.type}
                            onChange={e => editBox(b.id, { type: e.target.value })}>
                            {PALETTE_BOXES.map(x => <option key={x.type} value={x.type}>{x.label}</option>)}
                          </StuiSelect>
                        </td>
                        <td>
                          {b.type === 'value' ? (
                            <input className="stui-field-input" style={{ ...LIGHT_FIELD,  fontSize: 11 }} value={b.fieldSource || ''}
                              placeholder="Table.Field / admin:Table.Field / dsvalues"
                              onChange={e => {
                                const fs = e.target.value;
                                const parts = splitFieldSource(fs);
                                editBox(b.id, { fieldSource: fs, fieldTable: parts.table, fieldName: parts.name });
                              }} />
                          ) : (
                            <input className="stui-field-input" style={{ ...LIGHT_FIELD,  fontSize: 11 }} value={b.label || ''}
                              onChange={e => editBox(b.id, { label: e.target.value })} />
                          )}
                        </td>
                        <td>
                          <input className="stui-field-input" style={{ ...LIGHT_FIELD,  fontSize: 11 }} type="number" min={1} max={12}
                            value={b.colSpan ?? 12}
                            onChange={e => editBox(b.id, { colSpan: Math.max(1, Math.min(12, Number(e.target.value) || 1)) })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="stui-label-xs-muted">
                Widths set here are taken as-is and are not rebalanced, so a row can add up to something other than 12.
              </p>
            </>
          )}
        </div>

        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>{mode === 'grid' ? 'Done' : 'Cancel'}</button>
          {mode === 'repeat'  && <button className="stui-btn stui-btn-primary" onClick={doRepeat}  disabled={!srcLine || targetLines.length === 0}>Stamp {targetLines.length} Row{targetLines.length !== 1 ? 's' : ''}</button>}
          {mode === 'library' && <button className="stui-btn stui-btn-primary" onClick={doLibrary} disabled={libPicked.length === 0 || free.length === 0}>Add {libPicked.length} Row{libPicked.length !== 1 ? 's' : ''}</button>}
          {mode === 'paste'   && <button className="stui-btn stui-btn-primary" onClick={doPaste}   disabled={parsed.length === 0 || free.length === 0}>Add {Math.min(parsed.length, free.length)} Row{Math.min(parsed.length, free.length) !== 1 ? 's' : ''}</button>}
        </div>
      </div>
    </div>,
    document.body
  );
}

// What a box reads as on the template canvas: a field binding shows the field, a user-entered
// box says so (and names its dropdown list), a calc box names its function.
function canvasText(box) {
  const src = box.fieldSource;
  if (box.type === 'value' && isDsValues(src)) {
    const list = listNameOf(src);
    return list ? `List: ${list}` : 'User Defined';
  }
  if (box.type === 'value' && isCalc(src)) return `= ${parseCalc(src)?.fn ?? src}`;
  if (box.type === 'value' && src) return src;
  return box.label || box.type;
}

// Space between the boxes on one canvas row, in px.
const BOX_GAP = 3;

// Everything in a CanvasBox except its text, with the gear showing: 5px padding each side,
// grip 9, gear and remove buttons 11 each, and a BOX_GAP between each of the four items.
const BOX_CHROME = 10 + 9 + 11 + 11 + 3 * BOX_GAP;

const DOT_CHROME = 6 + BOX_GAP;   // the unsaved dot and its gap

const DROP_EDGE  = '#4f8ef7';

function CanvasBox({ box, gapShare = 0, dropEdge = null, readOnly = false, onRemove, onEdit, onDragStart, onDragEnd, onDragOver }) {
  const unsaved   = isUnsaved(box);
  const isSection = box.type === 'section';
  const text      = canvasText(box);

  // A narrow box drops its gear rather than letting the icon push the box past the row.
  // Double-clicking the box still opens the same edit popup. Measured, not guessed from
  // colSpan, because a twelfth is a different width in a header cell and a body cell.
  // The text is measured on its own inline span, so hiding the gear (which widens the text's
  // flex slot) cannot flip the answer back and forth.
  const boxRef  = useRef(null);
  const textRef = useRef(null);
  const [showGear, setShowGear] = useState(true);
  useLayoutEffect(() => {
    const el = boxRef.current;
    const txt = textRef.current;
    if (!el || !txt || typeof ResizeObserver === 'undefined') return undefined;
    const fit = () => setShowGear(el.clientWidth >= txt.offsetWidth + BOX_CHROME + (unsaved ? DOT_CHROME : 0));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, unsaved]);

  const edge = dropEdge === 'before' ? `inset 3px 0 0 ${DROP_EDGE}`
             : dropEdge === 'after'  ? `inset -3px 0 0 ${DROP_EDGE}` : undefined;

  return (
    <div
      ref={boxRef}
      draggable={!readOnly}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDoubleClick={() => { if (!readOnly) onEdit(box); }}
      title="Double-click to edit"
      style={{
        // Fixed fraction of the 12-column grid, NOT flex-grow. `flex: N` gives a 0 basis, so
        // padding and the border sit OUTSIDE the proportional split and the boundary shifts with
        // however many cells the row happens to have. borderBox pulls them inside, so a colSpan
        // of 6 always ends at exactly 50% and verticals line up row to row.
        // gapShare takes this box's share of the gaps between boxes out of its basis, so the
        // spans plus the gaps come to exactly the row and a full row never spills past its cell.
        flexGrow:     0,
        flexShrink:   0,
        flexBasis:    gapShare ? `calc(${((box.colSpan || 12) / 12) * 100}% - ${gapShare}px)` : `${((box.colSpan || 12) / 12) * 100}%`,
        minWidth:     0,
        display:      'flex',
        alignItems:   'center',
        gap:          BOX_GAP,
        padding:      '1px 5px',
        background:   unsaved ? '#fff59d' : isSection ? '#e0e0e0' : '#fff',
        border:       `1px solid ${unsaved ? '#f9a825' : '#000'}`,
        boxShadow:    edge,
        borderRadius: 0,
        cursor:       'grab',
        userSelect:   'none',
        height:       ROW_H - 4,
        boxSizing:    'border-box',
        overflow:     'hidden',
      }}
    >
      <GripVertical size={9} style={{ color: '#bbb', flexShrink: 0 }} />
      <span style={{
        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontSize: 9,
        color:         isSection ? '#222' : '#444',
        fontWeight:    isSection ? 600 : undefined,
        textTransform: isSection ? 'uppercase' : undefined,
      }}>
        <span ref={textRef}>{text}</span>
      </span>
      {unsaved && <span style={{ fontSize: 8, color: '#f9a825', fontWeight: 700, flexShrink: 0 }}>●</span>}
      {showGear && !readOnly && (
        <button onClick={e => { e.stopPropagation(); onEdit(box); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: '0 1px', lineHeight: 1, flexShrink: 0 }} title="Edit">
          <Settings2 size={9} />
        </button>
      )}
      {!readOnly && <button onClick={e => { e.stopPropagation(); onRemove(box.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: '0 1px', lineHeight: 1, flexShrink: 0 }} title="Remove">
        <X size={9} />
      </button>}
    </div>
  );
}

// readOnly (a published template): the layout shows, but nothing can be dragged, added, edited or
// removed, and any stray state update is dropped.
export function DSCanvas({ boxes, setBoxes: setBoxesRaw, sheetCount = 0, layout = null, allBoxes = null, readOnly = false }) {
  const setBoxes = readOnly ? () => {} : setBoxesRaw;
  const [draggedBox,     setDraggedBox]     = useState(null);
  const [dragOverHeader, setDragOverHeader] = useState(null); // lineNo 1-10
  const [dragOverCenter, setDragOverCenter] = useState(null); // slot 1|2|3
  const [dragOverBody,        setDragOverBody]        = useState(null); // lineNo 11-108
  const [dragOverSectionCell, setDragOverSectionCell] = useState(null); // { lineNo, colIdx }
  // The box edge a drop would land against: { zone, lineNo, targetId, after }. Set while the
  // pointer is over a box on a header or body row; null means append to the end of the row.
  const [dropSlot,            setDropSlot]            = useState(null);
  const [editingBox,          setEditingBoxRaw]       = useState(null);
  const setEditingBox = readOnly ? () => {} : setEditingBoxRaw;

  const centerList = getCenterBoxes(boxes);
  // The spec places the cal/comp blocks per sheet; without one, draw the classic 110-124 shell
  // only where the template has content for it.
  const sections = sectionRows(layout) ?? (hasStaticContent(boxes) ? STATIC_SECTIONS : []);

  // ── DnD: start ───────────────────────────────────────────────────────────────

  const paletteDragStart = (e, item) => {
    if (readOnly) { e.preventDefault(); return; }
    // `chip` is palette presentation only - it must not ride along onto the box.
    const { chip, ...boxDefaults } = item;
    setDraggedBox({ ...boxDefaults, id: null, isNew: true, colSpan: 12, fieldSource: '' });
    e.dataTransfer.effectAllowed = 'move';
  };

  const canvasDragStart = (e, box) => {
    if (readOnly) { e.preventDefault(); return; }
    setDraggedBox({ ...box, isNew: false });
    e.dataTransfer.setData('text/plain', String(box.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Only a box or palette chip dragged from this canvas may drop. Anything else (highlighted text,
  // a file) and every drag on a read-only canvas is refused before the drop zones see it, so the
  // pointer never offers a drop that would do nothing.
  const refuseForeignDrag = e => {
    if (!readOnly && draggedBox) return;
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'none';
  };

  const dragEnd = () => {
    setDraggedBox(null);
    setDragOverHeader(null);
    setDragOverCenter(null);
    setDragOverBody(null);
    setDragOverSectionCell(null);
    setDropSlot(null);
  };

  // Over a box, the half the pointer is in picks the edge: left half inserts before it, right
  // half after. The row cell's own dragover still runs (this does not stop propagation), so
  // the row keeps its drop highlight too.
  const boxDragOver = (e, box) => {
    e.preventDefault();
    const rect  = e.currentTarget.getBoundingClientRect();
    const after = e.clientX > rect.left + rect.width / 2;
    setDropSlot(s => (s && s.targetId === box.id && s.after === after && s.lineNo === box.lineNo && s.zone === box.zone
      ? s : { zone: box.zone, lineNo: box.lineNo, targetId: box.id, after }));
  };

  // A row cell forgets a slot that belongs to another row, so the indicator never lingers on
  // a row the pointer has left.
  const rowDragOver = (zone, lineNo) =>
    setDropSlot(s => (s && (s.zone !== zone || s.lineNo !== lineNo) ? null : s));

  // Where a drop on this row goes: { index } among the row's other boxes (null = append), or
  // { self: true } when the pointer is on the dragged box itself, which leaves it where it is.
  const resolveSlot = (zone, lineNo) => {
    if (!dropSlot || !draggedBox || dropSlot.zone !== zone || dropSlot.lineNo !== lineNo) return { index: null };
    if (!draggedBox.isNew && dropSlot.targetId === draggedBox.id) return { self: true };
    const others = boxes.filter(b => b.zone === zone && b.lineNo === lineNo && b.id !== draggedBox.id).sort(rowOrder);
    const at = others.findIndex(b => b.id === dropSlot.targetId);
    return { index: at < 0 ? null : at + (dropSlot.after ? 1 : 0) };
  };

  const dropEdgeFor = (box) => {
    if (!dropSlot || dropSlot.targetId !== box.id || draggedBox?.id === box.id) return null;
    return dropSlot.after ? 'after' : 'before';
  };

  // ── DnD: drop into header row (lineNo 1-10) ──────────────────────────────────

  const dropHeader = (e, lineNo) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverHeader(null);
    setDropSlot(null);
    if (!draggedBox) return;
    const slot = resolveSlot('header', lineNo);
    if (slot.self) { setDraggedBox(null); return; }
    const existing = getHeaderRow(boxes, lineNo);
    const newBox = {
      id:          draggedBox.isNew ? newBoxId() : draggedBox.id,
      type:        draggedBox.type,
      label:       draggedBox.label,
      fieldSource: draggedBox.fieldSource || '',
      fieldTable:  draggedBox.fieldTable  || '',
      fieldName:   draggedBox.fieldName   || '',
      zone:        'header',
      lineNo,
      order:       existing.length + 1,
      colSpan:     draggedBox.colSpan || 12,
    };
    setBoxes(prev => placeBox(prev, draggedBox, newBox, slot.index));
    setDraggedBox(null);
  };

  // ── DnD: drop into center slot (slotOrder = 1|2|3) ──────────────────────────

  const dropCenter = (e, slotOrder) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCenter(null);
    if (!draggedBox) return;
    const newBox = {
      id:          draggedBox.isNew ? newBoxId() : draggedBox.id,
      type:        draggedBox.type,
      label:       draggedBox.label,
      fieldSource: draggedBox.fieldSource || '',
      fieldTable:  draggedBox.fieldTable  || '',
      fieldName:   draggedBox.fieldName   || '',
      zone:        'center',
      lineNo:      0,
      order:       slotOrder,
      colSpan:     12,
    };
    setBoxes(prev => {
      const cleared = prev.filter(b => !(b.zone === 'center' && (b.order || 1) === slotOrder) && b.id !== draggedBox.id);
      const next = [...cleared, newBox];
      return draggedBox.isNew ? next : rebalanceRow(next, draggedBox.zone, draggedBox.lineNo);
    });
    setDraggedBox(null);
  };

  // ── DnD: drop into body line (lineNo 11-108) ─────────────────────────────────

  const dropBody = (e, lineNo) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverBody(null);
    setDropSlot(null);
    if (!draggedBox) return;
    const slot = resolveSlot('body', lineNo);
    if (slot.self) { setDraggedBox(null); return; }
    const newBox = {
      id:          draggedBox.isNew ? newBoxId() : draggedBox.id,
      type:        draggedBox.type,
      label:       draggedBox.label,
      fieldSource: draggedBox.fieldSource || '',
      fieldTable:  draggedBox.fieldTable  || '',
      fieldName:   draggedBox.fieldName   || '',
      zone:        'body',
      lineNo,
      order:       boxes.filter(b => b.zone === 'body' && b.lineNo === lineNo && b.id !== draggedBox.id).length + 1,
      colSpan:     draggedBox.colSpan || 12,
    };
    setBoxes(prev => placeBox(prev, draggedBox, newBox, slot.index));
    setDraggedBox(null);
  };

  // ── DnD: drop value/label into section data cell ─────────────────────────────

  const dropSectionCell = (e, lineNo, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSectionCell(null);
    if (!draggedBox || SECTION_TYPES.has(draggedBox.type)) return;
    const newBox = {
      id:          draggedBox.isNew ? newBoxId() : draggedBox.id,
      type:        draggedBox.type,
      label:       draggedBox.label,
      fieldSource: draggedBox.fieldSource || '',
      fieldTable:  draggedBox.fieldTable  || '',
      fieldName:   draggedBox.fieldName   || '',
      zone:        'section-cell',
      lineNo,
      order:       colIdx + 1,
      colSpan:     12,
    };
    setBoxes(prev => {
      const cleared = prev.filter(b => !(b.zone === 'section-cell' && b.lineNo === lineNo && b.order === colIdx + 1) && b.id !== draggedBox.id);
      const next = [...cleared, newBox];
      return draggedBox.isNew ? next : rebalanceRow(next, draggedBox.zone, draggedBox.lineNo);
    });
    setDraggedBox(null);
  };

  const removeBox = id => setBoxes(prev => {
    const gone = prev.find(b => b.id === id);
    const next = prev.filter(b => b.id !== id);
    return gone ? rebalanceRow(next, gone.zone, gone.lineNo) : next;
  });

  const editSave = updated => setBoxes(prev => {
    const dirty = { ...updated, _dirty: true };
    return redistributeAround(prev.map(b => b.id === dirty.id ? dirty : b), dirty);
  });

  // ── Section row renderer ──────────────────────────────────────────────────────

  const renderSectionRow = (row) => {
    const cells          = getSectionCells(boxes, row.lineNo);
    const isLastSection  = row.lineNo === sections[sections.length - 1].lineNo;
    const bBot           = isLastSection ? THICK : THIN;
    // Each block opens on a bold rule: the cal header sits under the body, the comp header under
    // the last cal row, whose own bottom is thin, so the THICK has to be asked for here.
    const bTop           = SECTION_HEADS.has(row.type) ? THICK : THIN;

    const dataCell = (colWidths, colIdx) => {
      const cellBox    = cells.find(b => b.order === colIdx + 1) || null;
      const isCellOver = dragOverSectionCell && dragOverSectionCell.lineNo === row.lineNo && dragOverSectionCell.colIdx === colIdx;
      const unsaved    = cellBox && isUnsaved(cellBox);
      return (
        // No width here: the nested section table's colgroup owns the geometry now, so this
        // div just fills its cell. Fixed pixel widths had to sum to whatever the colSpan-4
        // cell happened to be, and the padding pushed the last column past its bold border.
        <div key={colIdx}
          style={{
            width: '100%', minWidth: 0, height: '100%', boxSizing: 'border-box',
            background: isCellOver ? '#f0f6ff' : (unsaved ? '#fff59d' : '#fff'),
            display: 'flex', alignItems: 'center', padding: '0 3px',
            outline: isCellOver ? '1.5px dashed #4f8ef7' : undefined, overflow: 'hidden',
          }}
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverSectionCell({ lineNo: row.lineNo, colIdx }); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverSectionCell(null); }}
          onDrop={e => dropSectionCell(e, row.lineNo, colIdx)}
          onDoubleClick={() => cellBox && setEditingBox(cellBox)}
        >
          {cellBox ? (
            <>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9 }}>
                {canvasText(cellBox)}
              </span>
              {unsaved && <span style={{ fontSize: 8, color: '#f9a825', flexShrink: 0 }}>●</span>}
              {!readOnly && (
                <button onClick={e => { e.stopPropagation(); removeBox(cellBox.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, lineHeight: 1, flexShrink: 0 }}>
                  <X size={8} />
                </button>
              )}
            </>
          ) : (
            <span className="stui-label-xs-muted" style={{ fontStyle: 'italic', margin: 'auto' }}>drop here</span>
          )}
        </div>
      );
    };

    /**
     * Static sections render as a NESTED TABLE at width:100% with tableLayout:fixed, so the
     * browser enforces the column boundaries inside the colSpan-4 cell. Previously these were
     * flex rows of fixed pixel widths that had to sum to that cell's real width; any padding or
     * divider pushed the last column past the cell's bold right border and knocked the verticals
     * out of line with the rows above and the footer.
     *
     * Percentages of the group's own total, so a subheader and its data rows always share the
     * same boundaries whatever width the cell actually resolves to.
     */
    const sectionTable = (widths, renderCell, cellStyle = {}) => {
      const totalW = widths.reduce((sum, w) => sum + w, 0);
      return (
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%', ...FONT }}>
          <colgroup>
            {widths.map((w, i) => <col key={i} style={{ width: `${(w / totalW) * 100}%` }} />)}
          </colgroup>
          <tbody>
            <tr style={{ height: ROW_H }}>
              {widths.map((w, ci) => (
                <td key={ci} style={{ height: ROW_H, borderRight: ci < widths.length - 1 ? THIN : 'none', padding: 0, overflow: 'hidden', ...cellStyle }}>
                  {renderCell(ci)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      );
    };

    // Group-header boundaries are the CAL_COLS boundaries grouped up, so they align with the
    // subheader and data rows beneath them.
    const calGroups = [
      ['CALIBRATIONS AND TEST', CAL_COLS[0] + CAL_COLS[1]],
      ['INPUT OR TEST',         CAL_COLS[2] + CAL_COLS[3]],
      ['ACTION',                CAL_COLS[4]],
      ['OUTPUT OR SCALE',       CAL_COLS[5] + CAL_COLS[6]],
    ];

    const shellTd  = { border: THIN, borderTop: bTop, borderBottom: bBot, borderRight: THICK, padding: 0, verticalAlign: 'middle' };
    const headCell = { background: '#e4e4e4', textAlign: 'center', fontWeight: 600, fontSize: '7pt', verticalAlign: 'middle' };

    let contentTd;
    if (row.type === 'cal-group-header') {
      contentTd = <td colSpan={4} style={shellTd}>{sectionTable(calGroups.map(g => g[1]), ci => calGroups[ci][0], headCell)}</td>;
    } else if (row.type === 'cal-subheader') {
      contentTd = <td colSpan={4} style={shellTd}>{sectionTable(CAL_COLS, ci => CAL_HEADS[ci], { ...headCell, padding: '0 2px' })}</td>;
    } else if (row.type === 'cal-data') {
      contentTd = <td colSpan={4} style={shellTd}>{sectionTable(CAL_COLS, ci => dataCell(CAL_COLS, ci))}</td>;
    } else if (row.type === 'comp-header') {
      contentTd = <td colSpan={4} style={{ ...shellTd, ...headCell }}>COMPONENT IDENTIFICATIONS</td>;
    } else if (row.type === 'comp-subheader') {
      contentTd = <td colSpan={4} style={shellTd}>{sectionTable(COMP_COLS, ci => COMP_HEADS[ci], { ...headCell, padding: '0 2px' })}</td>;
    } else if (row.type === 'comp-data') {
      contentTd = <td colSpan={4} style={shellTd}>{sectionTable(COMP_COLS, ci => dataCell(COMP_COLS, ci))}</td>;
    } else {
      contentTd = <td colSpan={4} style={{ border: THIN, borderTop: bTop, borderBottom: bBot, borderRight: THICK }} />;
    }

    return (
      <tr key={`sect-${row.lineNo}`} style={{ height: ROW_H }}>
        <td style={lineNoCell({ borderTop: bTop, borderBottom: bBot })}>{row.lineNo}</td>
        <td style={revCell({ borderTop: bTop, borderBottom: bBot })}>A</td>
        {contentTd}
      </tr>
    );
  };

  // ── Cell style helpers ────────────────────────────────────────────────────────

  const lineNoCell = (extra = {}) => ({
    ...base, width: 28, textAlign: 'center',
    border: THIN, borderLeft: THICK, color: '#555', ...extra,
  });
  const revCell = (extra = {}) => ({
    ...base, width: 14, textAlign: 'center', border: THIN, color: '#888', ...extra,
  });

  const dropHint = (
    <span className="stui-label-xs-muted" style={{ fontStyle: 'italic' }}>drop here</span>
  );

  // The row clips as a last guard; the gap-aware basis is what keeps a full row inside it.
  const lineContent = (lineBoxes) => (
    lineBoxes.length === 0 ? dropHint : (
      <div style={{ display: 'flex', gap: BOX_GAP, alignItems: 'stretch', height: ROW_H - 4, minWidth: 0, overflow: 'hidden' }}>
        {lineBoxes.map(box => (
          <CanvasBox
            key={box.id}
            box={box}
            gapShare={(BOX_GAP * (lineBoxes.length - 1)) / lineBoxes.length}
            dropEdge={dropEdgeFor(box)}
            readOnly={readOnly}
            onRemove={removeBox}
            onEdit={setEditingBox}
            onDragStart={e => canvasDragStart(e, box)}
            onDragEnd={dragEnd}
            onDragOver={e => boxDragOver(e, box)}
          />
        ))}
      </div>
    )
  );

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden', gap: 16 }}
      onDragOverCapture={refuseForeignDrag} onDropCapture={refuseForeignDrag}>

      {/* ── Palette ─────────────────────────────────────────────────────────── */}
      {!readOnly && <div style={{ width: 140, flexShrink: 0, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div className="stui-help-section-heading" style={{ marginBottom: 4 }}>Box Types</div>
        {PALETTE_CHIPS.map(item => (
          <div key={item.chip || item.type} draggable onDragStart={e => paletteDragStart(e, item)} onDragEnd={dragEnd}
            /* background is pinned white, so the colour has to be pinned too - otherwise the
               text inherits the page's theme colour, which is near-white in Buckeye and
               Mariners and disappears against the chip. */
            style={{ ...LIGHT_FIELD, padding: '6px 10px', border: '1px solid #000', borderRadius: 0, cursor: 'grab', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, userSelect: 'none' }}>
            <GripVertical size={12} style={{ color: '#aaa' }} />
            {item.chip || item.label}
          </div>
        ))}
        <div className="stui-label-xs-muted" style={{ marginTop: 10, lineHeight: 1.6 }}>
          Drop boxes onto any cell. Double-click to edit.
        </div>
      </div>}

      {/* ── ISA grid canvas ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', paddingTop: 24, paddingBottom: 40, background: '#c8c8c8' }}>
        {/* The sheet is paper: it stays light and its text stays black in every theme.
            stui-rpt-preview-container pins --color-* to their light values (the same
            trick the report preview uses), which keeps the grey "drop here" hints
            legible instead of letting --color-gray-400 darken into the white page.
            Its overflow:hidden is re-opened here so nothing on the sheet gets clipped. */}
        <div className="stui-rpt-preview-container"
          style={{ background: '#fff', color: '#222', overflow: 'visible', boxShadow: '0 3px 18px rgba(0,0,0,0.28)', padding: '24px 28px', width: TABLE_W + 56, boxSizing: 'border-box', alignSelf: 'flex-start' }}>

          {/* ── HEADER TABLE: lines 1-10 + center (3 equal panels, 7 cols) ─── */}
          <table style={{ borderCollapse: 'collapse', width: TABLE_W, tableLayout: 'fixed', background: '#fff', ...FONT }}>
            <colgroup>
              <col style={{ width: 28  }} />
              <col style={{ width: 14  }} />
              <col style={{ width: 186 }} />
              <col style={{ width: 228 }} />
              <col style={{ width: 28  }} />
              <col style={{ width: 14  }} />
              <col style={{ width: 186 }} />
            </colgroup>
            <tbody>
              {[0,1,2,3,4].map(i => {
                const lNo    = i + 1;
                const rNo    = i + 6;
                const isFirst = i === 0;
                const isLast  = i === 4;
                const lBoxes    = getHeaderRow(boxes, lNo);
                const rBoxes    = getHeaderRow(boxes, rNo);
                const lOver     = dragOverHeader === lNo;
                const rOver     = dragOverHeader === rNo;
                const lUnsaved  = lBoxes.some(isUnsaved);
                const rUnsaved  = rBoxes.some(isUnsaved);

                return (
                  <tr key={`h${lNo}`} style={{ height: ROW_H }}>

                    {/* Left: line lNo */}
                    <td style={lineNoCell({ borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN })}>{lNo}</td>
                    <td style={revCell({ borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN })}>A</td>
                    <td
                      style={{
                        ...base, border: THIN,
                        borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN,
                        background: lOver ? '#f0f6ff' : lUnsaved ? '#fff59d' : undefined,
                        outline: lOver ? '1.5px dashed #4f8ef7' : undefined,
                        padding: '1px 4px',
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverHeader(lNo); rowDragOver('header', lNo); }}
                      onDragLeave={() => setDragOverHeader(null)}
                      onDrop={e => dropHeader(e, lNo)}
                    >
                      {lineContent(lBoxes)}
                    </td>

                    {/* Center: rowspan 5, top 25% / middle 50% / bottom 25% */}
                    {isFirst && (
                      <td rowSpan={5} style={{ border: THIN, borderTop: THICK, borderBottom: THICK, padding: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', height: ROW_H * 5 }}>
                          {[
                            { slot: 1, flex: 1 },
                            { slot: 2, flex: 2, wrap: true },
                            { slot: 3, flex: 1 },
                          ].map(({ slot, flex, wrap }) => {
                            const slotBox = centerList.find(b => (b.order || 1) === slot) || null;
                            const sOver   = dragOverCenter === slot;
                            const unsaved = slotBox && isUnsaved(slotBox);
                            return (
                              <div
                                key={slot}
                                style={{
                                  flex, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  padding: '0 4px', gap: 3,
                                  borderBottom: slot < 3 ? THIN : 'none',
                                  outline: sOver ? '1.5px dashed #4f8ef7' : undefined,
                                  background: sOver ? '#f0f6ff' : unsaved ? '#fff59d' : undefined,
                                  cursor: slotBox ? 'pointer' : 'default',
                                  overflow: 'hidden',
                                  boxSizing: 'border-box',
                                }}
                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverCenter(slot); }}
                                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCenter(null); }}
                                onDrop={e => dropCenter(e, slot)}
                                onDoubleClick={() => slotBox && setEditingBox(slotBox)}
                              >
                                {slotBox ? (
                                  <>
                                    <GripVertical size={9} style={{ color: '#bbb', flexShrink: 0 }} />
                                    <span style={{
                                      ...FONT, flex: 1, fontSize: 9, textAlign: 'center',
                                      overflow: 'hidden',
                                      ...(wrap
                                        ? { whiteSpace: 'normal', wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
                                        : { whiteSpace: 'nowrap', textOverflow: 'ellipsis' }),
                                    }}>
                                      {canvasText(slotBox)}
                                    </span>
                                    {unsaved && <span style={{ fontSize: 8, color: '#f9a825', flexShrink: 0 }}>●</span>}
                                    {!readOnly && <button onClick={e => { e.stopPropagation(); setEditingBox(slotBox); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, lineHeight: 1, flexShrink: 0 }} title="Edit"><Settings2 size={9} /></button>}
                                    {!readOnly && <button onClick={e => { e.stopPropagation(); removeBox(slotBox.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, lineHeight: 1, flexShrink: 0 }} title="Remove"><X size={9} /></button>}
                                  </>
                                ) : (
                                  <span className="stui-label-xs-muted" style={{ fontStyle: 'italic' }}>drop here</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    )}

                    {/* Right: line rNo */}
                    <td style={lineNoCell({ borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN })}>{rNo}</td>
                    <td style={revCell({ borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN })}>A</td>
                    <td
                      style={{
                        ...base, border: THIN, borderRight: THICK,
                        borderTop: isFirst ? THICK : THIN, borderBottom: isLast ? THICK : THIN,
                        background: rOver ? '#f0f6ff' : rUnsaved ? '#fff59d' : undefined,
                        outline: rOver ? '1.5px dashed #4f8ef7' : undefined,
                        padding: '1px 4px',
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverHeader(rNo); rowDragOver('header', rNo); }}
                      onDragLeave={() => setDragOverHeader(null)}
                      onDrop={e => dropHeader(e, rNo)}
                    >
                      {lineContent(rBoxes)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── BODY TABLE: left / right columns, ranges from the sheet's spec (bodyRowPairs) ─ */}
          <table style={{ borderCollapse: 'collapse', width: TABLE_W, tableLayout: 'fixed', background: '#fff', ...FONT }}>
            <colgroup>
              <col style={{ width: 28  }} />  {/* L lineNo  */}
              <col style={{ width: 14  }} />  {/* L rev     */}
              <col style={{ width: 300 }} />  {/* L content */}
              <col style={{ width: 28  }} />  {/* R lineNo  */}
              <col style={{ width: 14  }} />  {/* R rev     */}
              <col style={{ width: 300 }} />  {/* R content */}
            </colgroup>
            <tbody>
              {bodyRowPairs(boxes, sheetCount, layout).map(({ leftLine, rightLine }, i, arr) => {
                const lBoxes    = leftLine  == null ? [] : getBodyLine(boxes, leftLine);
                const rBoxes    = rightLine == null ? [] : getBodyLine(boxes, rightLine);
                // `!= null` guard matters: a short column yields a null line, and when nothing
                // is being dragged dragOverBody is also null - so a bare === lit every spare
                // row up with the drag-target outline.
                const lOver     = leftLine  != null && dragOverBody === leftLine;
                const rOver     = rightLine != null && dragOverBody === rightLine;
                const lUnsaved  = lBoxes.some(isUnsaved);
                const rUnsaved  = rBoxes.some(isUnsaved);
                const isLast    = i === arr.length - 1;

                return (
                  <tr key={i} style={{ height: ROW_H }}>

                    {/* Left panel */}
                    <td style={lineNoCell({ borderTop: 'none', borderBottom: isLast ? THICK : THIN, color: lOver ? '#4f8ef7' : '#555' })}>{leftLine}</td>
                    <td style={revCell({ borderTop: 'none', borderBottom: isLast ? THICK : THIN })}>A</td>
                    <td
                      style={{
                        border: THIN, borderTop: 'none', borderBottom: isLast ? THICK : THIN,
                        padding: '1px 4px', verticalAlign: 'middle',
                        background: lOver ? '#f0f6ff' : lUnsaved ? '#fff59d' : undefined,
                        outline: lOver ? '1.5px dashed #4f8ef7' : undefined,
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverBody(leftLine); rowDragOver('body', leftLine); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverBody(null); }}
                      onDrop={e => dropBody(e, leftLine)}
                    >
                      {lineContent(lBoxes)}
                    </td>

                    {/* Right panel */}
                    <td style={lineNoCell({ borderTop: 'none', borderBottom: isLast ? THICK : THIN, borderLeft: THICK, color: rOver ? '#4f8ef7' : '#555' })}>{rightLine}</td>
                    <td style={revCell({ borderTop: 'none', borderBottom: isLast ? THICK : THIN })}>A</td>
                    <td
                      style={{
                        border: THIN, borderTop: 'none', borderBottom: isLast ? THICK : THIN, borderRight: THICK,
                        padding: '1px 4px', verticalAlign: 'middle',
                        background: rOver ? '#f0f6ff' : rUnsaved ? '#fff59d' : undefined,
                        outline: rOver ? '1.5px dashed #4f8ef7' : undefined,
                      }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverBody(rightLine); rowDragOver('body', rightLine); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverBody(null); }}
                      onDrop={e => dropBody(e, rightLine)}
                    >
                      {lineContent(rBoxes)}
                    </td>
                  </tr>
                );
              })}
              {sections.map(row => renderSectionRow(row))}
            </tbody>
          </table>

          {/* ── REVISION FOOTER TABLE ──────────────────────────────────────── */}
          <table style={{ borderCollapse: 'collapse', width: TABLE_W, ...FONT, borderTop: THICK }}>
            <colgroup>
              {REV_COLS.map((c, i) => <col key={i} style={c.width ? { width: c.width } : {}} />)}
            </colgroup>
            <tbody>
              <tr style={{ background: '#f0f0f0' }}>
                {REV_COLS.map((c, ci) => (
                  <td key={ci} style={{
                    ...base,
                    height: 'auto', lineHeight: '14px',
                    padding: '2px 3px',
                    textAlign: 'center', fontWeight: 600, fontSize: '7.5pt',
                    whiteSpace: 'normal',
                    border: THIN,
                    borderLeft:  ci === 0        ? THICK : THIN,
                    borderRight: ci === REV_LAST ? THICK : THIN,
                  }}>
                    {Array.isArray(c.label)
                      ? c.label.map((l, li) => <div key={li}>{l}</div>)
                      : c.label}
                  </td>
                ))}
              </tr>
              {revRows(layout).map((ri, _, all) => (
                <tr key={ri} style={{ height: 18 }}>
                  {REV_COLS.map((_, ci) => (
                    <td key={ci} style={{
                      height: 18,
                      border: THIN,
                      borderLeft:   ci === 0        ? THICK : THIN,
                      borderRight:  ci === REV_LAST ? THICK : THIN,
                      borderBottom: ri === all.length - 1 ? THICK : THIN,
                    }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      </div>

      {editingBox && (
        <BoxEditModal box={editingBox} onSave={editSave} onClose={() => setEditingBoxRaw(null)} templateBoxes={allBoxes ?? boxes} />
      )}
    </div>
  );
}
